/**
 * AI Chat Store
 *
 * Zustand store for managing AI Chat UI state.
 */

import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import {
  AIChatMessage,
  ChatContext,
  PendingAction,
  SuggestedAction,
  DEFAULT_SUGGESTED_ACTIONS,
} from '../types/ai-chat.types';

/** Welchen Dialog der Chat öffnen möchte, samt Vorbefüllung aus dem Gespräch. */
export interface DialogRequest {
  /**
   * Welcher der VORHANDENEN Dialoge geoeffnet wird.
   *
   * Bewusst eine Aufzaehlung, kein freier String: Der Wirt muss jeden Namen kennen, und
   * ein Tippfehler im Backend soll beim Uebersetzen auffallen, nicht als stumme Karte
   * beim Nutzer.
   */
  dialog: 'create_campaign' | 'campaign_wizard' | 'blacklist';
  props: Record<string, unknown>;
  /** Macht jede Anfrage unterscheidbar — zweimal derselbe Klick soll erneut öffnen. */
  requestId: string;
}

export interface WizardState {
  active: boolean;
  type: 'campaign' | 'account' | null;
  currentStep: number;
  totalSteps: number;
  campaignId?: string;
  data: Record<string, unknown>;
}

interface AIChatStore {
  // UI State
  isOpen: boolean;
  isMinimized: boolean;
  isFullscreen: boolean;

  // Input State
  inputValue: string;
  isTyping: boolean;

  // Context
  context: ChatContext;

  // Pending Actions
  pendingAction: PendingAction | null;

  // Wizard State
  wizard: WizardState;

  // Suggested Actions
  suggestedActions: SuggestedAction[];

  // Local Messages (for streaming/optimistic updates)
  streamingMessage: AIChatMessage | null;

  // Workspace side panel (Schritt 1: Account-Daten, Schritt 2: LinkedIn verbinden)
  sidePanel: 'profile' | 'team' | 'license' | 'linkedin' | 'workhours' | 'limits' | 'signature' | 'blacklist' | null;
  // Message queued by a side panel (e.g. after saving profile data) that the
  // chat panel should send as the user's next message.
  queuedUserMessage: string | null;
  // Wird nach jedem abgeschlossenen Chat-Turn erhöht — Formulare im
  // Arbeitsbereich laden dann neu (der Bot kann Daten geändert haben).
  dataVersion: number;
  // Lizenz-Zuordnung je Benutzer aus dem Lizenz-Schritt (userId → saia_recruiting/saia_sales/saia_bundle)
  licenseAssignments: Record<string, string>;
  // Einrichtungs-Modal (Pflichtangaben) — öffnet automatisch, solange Daten fehlen
  setupModalOpen: boolean;

  /**
   * Vom Chat angeforderter UI-Dialog (Karte `open_dialog`).
   *
   * Der Chat baut die Fragen nicht nach, sondern öffnet das bestehende Fenster. Die
   * Anfrage liegt hier, weil Karte und Dialog-Wirt an verschiedenen Stellen im Baum
   * hängen: die Karte in der Nachrichtenliste, der Wirt im App-Rahmen — sonst würde der
   * Dialog mit der Nachricht verschwinden, sobald der Verlauf neu lädt.
   */
  dialogRequest: DialogRequest | null;

  // Actions
  setOpen: (isOpen: boolean) => void;
  toggle: () => void;
  minimize: () => void;
  maximize: () => void;
  toggleFullscreen: () => void;

  setInputValue: (value: string) => void;
  clearInput: () => void;
  setIsTyping: (isTyping: boolean) => void;

  setContext: (context: Partial<ChatContext>) => void;
  clearContext: () => void;

  setPendingAction: (action: PendingAction | null) => void;
  clearPendingAction: () => void;

  startWizard: (type: 'campaign' | 'account', totalSteps: number, campaignId?: string) => void;
  updateWizardStep: (step: number) => void;
  updateWizardData: (data: Record<string, unknown>) => void;
  endWizard: () => void;

  setSuggestedActions: (actions: SuggestedAction[]) => void;
  resetSuggestedActions: () => void;

  setStreamingMessage: (message: AIChatMessage | null) => void;
  appendToStreamingMessage: (content: string) => void;

  setSidePanel: (panel: 'profile' | 'team' | 'license' | 'linkedin' | 'workhours' | 'limits' | 'signature' | 'blacklist' | null) => void;
  bumpDataVersion: () => void;
  setLicenseAssignment: (userId: string, product: string) => void;
  setSetupModalOpen: (open: boolean) => void;
  requestDialog: (request: DialogRequest) => void;
  clearDialogRequest: () => void;
  queueUserMessage: (message: string) => void;
  clearQueuedUserMessage: () => void;

  reset: () => void;
}

const initialWizardState: WizardState = {
  active: false,
  type: null,
  currentStep: 0,
  totalSteps: 0,
  campaignId: undefined,
  data: {},
};

const useAIChatStore = createWithEqualityFn<AIChatStore>(
  (set, get) => ({
    // Initial UI State
    isOpen: false,
    isMinimized: false,
    isFullscreen: false,

    // Initial Input State
    inputValue: '',
    isTyping: false,

    // Initial Context
    context: {},

    // Initial Pending Action
    pendingAction: null,

    // Initial Wizard State
    wizard: initialWizardState,

    // Initial Suggested Actions
    suggestedActions: DEFAULT_SUGGESTED_ACTIONS,

    // Initial Streaming Message
    streamingMessage: null,

    // Initial Profile Panel State
    sidePanel: null,
    queuedUserMessage: null,
    dataVersion: 0,
    licenseAssignments: {},
    setupModalOpen: false,

    // Kein Dialog angefordert
    dialogRequest: null,

    // UI Actions
    setOpen: (isOpen) => set({ isOpen, isMinimized: false }),

    toggle: () => {
      const { isOpen, isMinimized } = get();
      if (isMinimized) {
        set({ isMinimized: false });
      } else {
        set({ isOpen: !isOpen });
      }
    },

    minimize: () => set({ isMinimized: true }),

    maximize: () => set({ isMinimized: false }),

    toggleFullscreen: () => set((state) => ({ isFullscreen: !state.isFullscreen })),

    // Input Actions
    setInputValue: (value) => set({ inputValue: value }),

    clearInput: () => set({ inputValue: '' }),

    setIsTyping: (isTyping) => set({ isTyping }),

    // Context Actions
    setContext: (newContext) =>
      set((state) => ({
        context: { ...state.context, ...newContext },
      })),

    clearContext: () => set({ context: {} }),

    // Pending Action Actions
    setPendingAction: (action) => set({ pendingAction: action }),

    clearPendingAction: () => set({ pendingAction: null }),

    // Wizard Actions
    startWizard: (type, totalSteps, campaignId) =>
      set({
        wizard: {
          active: true,
          type,
          currentStep: 1,
          totalSteps,
          campaignId,
          data: {},
        },
      }),

    updateWizardStep: (step) =>
      set((state) => ({
        wizard: { ...state.wizard, currentStep: step },
      })),

    updateWizardData: (data) =>
      set((state) => ({
        wizard: { ...state.wizard, data: { ...state.wizard.data, ...data } },
      })),

    endWizard: () => set({ wizard: initialWizardState }),

    // Suggested Actions
    setSuggestedActions: (actions) => set({ suggestedActions: actions }),

    resetSuggestedActions: () => set({ suggestedActions: DEFAULT_SUGGESTED_ACTIONS }),

    // Streaming Message Actions
    setSidePanel: (panel) => set({ sidePanel: panel }),

    bumpDataVersion: () => set((state) => ({ dataVersion: state.dataVersion + 1 })),

    setLicenseAssignment: (userId, product) =>
      set((state) => ({ licenseAssignments: { ...state.licenseAssignments, [userId]: product } })),

    setSetupModalOpen: (open) => set({ setupModalOpen: open }),

    // ─── Dialog-Anfragen aus dem Chat ────────────────────────────────────
    requestDialog: (request) => set({ dialogRequest: request }),

    clearDialogRequest: () => set({ dialogRequest: null }),

    queueUserMessage: (message) => set({ queuedUserMessage: message }),

    clearQueuedUserMessage: () => set({ queuedUserMessage: null }),

    setStreamingMessage: (message) => set({ streamingMessage: message }),

    appendToStreamingMessage: (content) =>
      set((state) => {
        if (!state.streamingMessage) return state;
        return {
          streamingMessage: {
            ...state.streamingMessage,
            content: state.streamingMessage.content + content,
          },
        };
      }),

    // Reset
    reset: () =>
      set({
        isOpen: false,
        isMinimized: false,
        isFullscreen: false,
        inputValue: '',
        isTyping: false,
        context: {},
        pendingAction: null,
        wizard: initialWizardState,
        suggestedActions: DEFAULT_SUGGESTED_ACTIONS,
        streamingMessage: null,
        sidePanel: null,
        queuedUserMessage: null,
        dialogRequest: null,
      }),
  }),
  shallow
);

export default useAIChatStore;

